// Where an outreach link lands after login. Shared by both `/auth/verify`
// entry points — the public VerifyToken page (no session) and the protected
// AuthVerifyRedirect (session already live) — so a link resolves to the same
// place either way.

// Discriminators up-api sends as `redirect`, as opposed to a literal path.
export const SECTION_REDIRECTS = ['swap', 'shop', 'upgrade', 'channel']

export interface PostLoginRefs {
  orderUid?: string | null
  instanceUid?: string | null
  channelUid?: string | null
  contentUid?: string | null
}

export const buildPostLoginPath = (
  redirect: string | null | undefined,
  refs: PostLoginRefs,
): string => {
  const { orderUid, instanceUid, channelUid, contentUid } = refs

  // `channel` goes straight to the channel rather than bouncing through the
  // wallet. The wallet hop meant the deep link only survived if the wallet had
  // already loaded bookings, and a stale `fnbOrder` in localStorage swallowed
  // it outright. The channel fetches whatever it needs by itself.
  if (redirect === 'channel' && channelUid) {
    const params = new URLSearchParams()
    if (orderUid) params.set('order', orderUid)
    if (instanceUid) params.set('instance', instanceUid)
    // The channel reads its FullScreen deep link from `content`.
    if (contentUid) params.set('content', contentUid)
    const query = params.toString()
    return `/channel/${channelUid}${query ? `?${query}` : ''}`
  }

  // swap / shop / upgrade still land on the wallet: their drawers and route
  // handlers live there. So does a `channel` redirect with no channel to open.
  if (redirect && SECTION_REDIRECTS.includes(redirect)) {
    const params = new URLSearchParams()
    params.set('section', redirect)
    if (orderUid) params.set('orderUid', orderUid)
    if (instanceUid) params.set('instanceUid', instanceUid)
    if (channelUid) params.set('channelUid', channelUid)
    if (contentUid) params.set('contentUid', contentUid)
    return `/tickets?${params.toString()}`
  }

  // A literal login-return path. It may arrive as a bare segment (e.g.
  // `tickets`); without a leading slash React Router resolves it relative to
  // /auth/verify (→ /auth/tickets, a 404).
  if (redirect) return redirect.startsWith('/') ? redirect : `/${redirect}`

  const params = new URLSearchParams()
  if (orderUid) params.set('orderUid', orderUid)
  if (channelUid) params.set('channelUid', channelUid)
  if (contentUid) params.set('contentUid', contentUid)
  const query = params.toString()
  return `/tickets${query ? `?${query}` : ''}`
}
